import { Portal } from "@headlessui/react";
import clsx from "clsx";
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import styles from "./style.module.scss";

const VIEWPORT_GUTTER = 8;
const PORTAL_Z_INDEX = 9999;

interface IProps {
  message: string | React.ReactNode;
  position?: "left" | "center" | "right" | "down" | "up";
  children: React.ReactNode;
  wrapperClass?: string;
  disabled?: boolean;
  tooltipContainerClassName?: string;
  handleMouseOver?: (event: React.MouseEvent) => void;
  handleMouseOut?: (event: React.MouseEvent) => void;
  open?: boolean;
  trigger?: "hover" | "click";
}

export default function Tooltip({
  message,
  position = "center",
  wrapperClass,
  children,
  disabled = false,
  tooltipContainerClassName,
  handleMouseOver,
  handleMouseOut,
  open = false,
  trigger = "hover"
}: Readonly<IProps>) {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const [tooltipNode, setTooltipNode] = useState<HTMLDivElement | null>(null);
  const [bounds, setBounds] = useState<{ offsetX: number; offsetY: number; width?: number }>({
    offsetX: 0,
    offsetY: 0
  });
  const [anchor, setAnchor] = useState<{
    left: number;
    top: number;
    width: number;
    height: number;
  } | null>(null);
  const naturalWidth = useRef(0);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const insideAnchor = containerRef.current?.contains(target);
      const insideTooltip = tooltipNode?.contains(target);
      if (!insideAnchor && !insideTooltip) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen, tooltipNode]);

  const handleMouseEnter = (e: React.MouseEvent) => {
    if (trigger !== "hover") return;
    if (!isOpen) {
      setIsOpen(true);
    }
    if (handleMouseOver) {
      handleMouseOver(e);
    }
  };

  const handleMouseLeave = (e: React.MouseEvent) => {
    if (trigger !== "hover") return;
    // Don't close on mouse leave - only close on outside click
    if (handleMouseOut) {
      handleMouseOut(e);
    }
  };

  const handleClick = () => {
    if (trigger !== "click") return;
    setIsOpen((prev) => !prev);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (trigger !== "click") return;
    if (e.key === "Enter" || e.key === " ") {
      if (e.key === " ") {
        e.preventDefault();
      }
      handleClick();
    }
  };

  const getWrapperClassByPosition = () => {
    switch (position) {
      case "left":
        return "right-0 pl-3 -mr-1.5";
      case "right":
        return "left-0 pr-3 -ml-1.5";
      case "down":
        return "top-10";
      case "up":
        return "bottom-10";
      default:
        return "left-1/2 -translate-x-1/2 px-3";
    }
  };

  const getTailClassByPosition = () => {
    switch (position) {
      case "left":
        return "right-0 mr-0.5";
      case "right":
        return "left-0 ml-0.5";
      case "down":
        return "top-0 mr-2";
      case "up":
        return "bottom-0 mb-2";
      default:
        return "";
    }
  };

  const clampIntoView = useCallback(() => {
    if (!tooltipNode || !containerRef.current) return;

    const rootStyle = getComputedStyle(document.documentElement);
    // useSafeArea publishes the measured Android insets as --safe-area-*-fixed; the
    // --safe-area-inset-* names are only ever written as a zeroed fallback.
    const inset = (...names: string[]) => {
      for (const name of names) {
        const value = parseFloat(rootStyle.getPropertyValue(name));
        if (Number.isFinite(value) && value > 0) return value;
      }
      return 0;
    };
    const bandLeft = inset("--safe-area-left-fixed", "--safe-area-inset-left") + VIEWPORT_GUTTER;
    const bandRight =
      window.innerWidth -
      inset("--safe-area-right-fixed", "--safe-area-inset-right") -
      VIEWPORT_GUTTER;
    const bandWidth = bandRight - bandLeft;
    const bandTop = inset("--safe-area-top-fixed", "--safe-area-inset-top") + VIEWPORT_GUTTER;
    const bandBottom =
      window.innerHeight -
      inset("--safe-area-bottom-fixed", "--safe-area-inset-bottom") -
      VIEWPORT_GUTTER;

    if (!naturalWidth.current) naturalWidth.current = tooltipNode.offsetWidth;
    const constrained = naturalWidth.current > bandWidth;
    const width = constrained ? bandWidth : naturalWidth.current;

    const container = containerRef.current.getBoundingClientRect();
    setAnchor((previous) =>
      previous &&
      previous.left === container.left &&
      previous.top === container.top &&
      previous.width === container.width &&
      previous.height === container.height
        ? previous
        : {
            left: container.left,
            top: container.top,
            width: container.width,
            height: container.height
          }
    );

    if (!anchor) return;

    // offsetLeft/offsetTop are measured against the portal host, so they stay put when the
    // host moves. That keeps the correction out of its own next measurement.
    const layoutLeft = container.left + tooltipNode.offsetLeft;
    const left = position === "center" ? layoutLeft - width / 2 : layoutLeft;
    const right = left + width;

    let offsetX = 0;
    if (right > bandRight) offsetX = bandRight - right;
    if (left + offsetX < bandLeft) offsetX = bandLeft - left;

    const height = tooltipNode.offsetHeight;
    const top = container.top + tooltipNode.offsetTop;
    let offsetY = 0;
    if (top + height > bandBottom) offsetY = bandBottom - (top + height);
    if (top + offsetY < bandTop) offsetY = bandTop - top;

    setBounds({ offsetX, offsetY, width: constrained ? bandWidth : undefined });
  }, [tooltipNode, position, anchor]);

  useLayoutEffect(() => {
    if (isOpen || open) {
      clampIntoView();
      return;
    }
    naturalWidth.current = 0;
    setBounds({ offsetX: 0, offsetY: 0 });
  }, [isOpen, open, clampIntoView]);

  useEffect(() => {
    if (!isOpen && !open) return;
    const handler = () => clampIntoView();
    const visual = window.visualViewport;
    window.addEventListener("resize", handler);
    window.addEventListener("scroll", handler, true);
    visual?.addEventListener("resize", handler);
    visual?.addEventListener("scroll", handler);
    return () => {
      window.removeEventListener("resize", handler);
      window.removeEventListener("scroll", handler, true);
      visual?.removeEventListener("resize", handler);
      visual?.removeEventListener("scroll", handler);
    };
  }, [isOpen, open, clampIntoView]);

  return (
    <div
      ref={containerRef}
      className={clsx(
        !tooltipContainerClassName && "flex flex-col",
        styles.tooltipContainer,
        tooltipContainerClassName
      )}>
      <div
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onClick={handleClick}
        {...(trigger === "click" ? { role: "button", tabIndex: 0, onKeyDown: handleKeyDown } : {})}>
        {children}
      </div>
      {!disabled && (
        <Portal>
          <div
            className={styles.tooltipContainer}
            style={{
              position: "fixed",
              left: (anchor?.left ?? 0) + bounds.offsetX,
              top: (anchor?.top ?? 0) + bounds.offsetY,
              width: anchor?.width ?? 0,
              height: anchor?.height ?? 0,
              pointerEvents: "none",
              zIndex: PORTAL_Z_INDEX
            }}>
            <div
              ref={setTooltipNode}
              style={bounds.width !== undefined ? { width: bounds.width, minWidth: 0 } : undefined}
              className={clsx(
                styles.wrapper,
                getWrapperClassByPosition(),
                isOpen || open ? "!scale-100 pointer-events-auto" : "scale-0 pointer-events-none"
              )}>
              <div className={clsx(styles.body, wrapperClass, "drop-shadow-[0_4px_4px_#A6A0A040]")}>
                <div className={styles.messageWrapper}>{message}</div>
                <div className={clsx(styles.tail, getTailClassByPosition())}></div>
              </div>
            </div>
          </div>
        </Portal>
      )}
    </div>
  );
}
